import {
    Component,
    Listen,
    Element,
    Host,
    Watch,
    h,
    State,
} from "@stencil/core";
import { SidebarTogglerButton } from "./sidebar-toggler-button";
interface TogglerButton {
    tabTarget: string;
    iconName: string;
    label: string;
}

/**
 * @slot (default) - insert the tabs and their content according to the documentation
 */

@Component({
    tag: "ux-iws-sidebar",
    styleUrl: "sidebar.scss",
})
export class Sidebar {
    private baseClass = "ux-iws-sidebar";
    private togglerContainer: HTMLDivElement;
    private togglerActiveClass: string = this.baseClass + "__active-toggler";
    private expandBreakpoint: number = 1024;

    @Element() element: HTMLUxIwsSidebarElement;

    @State() togglerReady: boolean = false;
    @State() active: boolean = false;
    @State() togglerButtons: TogglerButton[] = [];

    componentWillLoad() {
        this.setTogglerButtons();
    }

    componentDidLoad() {
        this.togglerReady = true;
    }

    private setTogglerButtons = () => {
        const togglerButtons: TogglerButton[] = [];
        const tabs = Array.from(this.element.querySelectorAll("ux-tab"));
        tabs.forEach((tab: HTMLUxTabElement) => {
            togglerButtons.push({
                iconName: tab.getAttribute("data-icon-name"),
                label: tab.getAttribute("name"),
                tabTarget: tab.getAttribute("tab-id"),
            });
        });
        this.togglerButtons = togglerButtons;
    };

    private triggerActiveState = (e: Event) => {
        const eTarget = e.target as HTMLElement;
        if (
            eTarget.classList.contains(this.togglerActiveClass) ||
            eTarget.closest(`.${this.togglerActiveClass}`)
        ) {
            this.active = false;
        } else {
            this.active = true;
        }
    };

    private expandTogglerButton = (e: Event) => {
        if (window.innerWidth < this.expandBreakpoint) return;
        const eTarget = e.target as HTMLElement;
        const descriptionText = eTarget.querySelector("span");
        descriptionText.style.width = descriptionText.scrollWidth + "px";
    };

    private retractTogglerButton = (e: Event) => {
        if (window.innerWidth < this.expandBreakpoint) return;
        const eTarget = e.target as HTMLElement;
        const descriptionText = eTarget.querySelector("span");
        descriptionText.style.width = "0px";
    };

    @Listen("uxActiveTabSet", { target: "window" })
    setActiveToggler(e: CustomEvent) {
        const eTarget = e.target as HTMLElement;
        if (!this.element.contains(eTarget)) return;
        if (this.togglerReady === false) return;
        if (this.active) {
            const activeTogglerId = e.detail;
            this.setActiveTogglerClass(activeTogglerId);
        }
    }

    private setActiveTogglerClass = (activeTogglerId: string) => {
        const togglers = Array.from(
            this.togglerContainer.querySelectorAll("button")
        );
        const activeToggler = this.togglerContainer.querySelector(
            `button[data-ux-tab-target=${activeTogglerId}]`
        );
        togglers.forEach((toggler) =>
            toggler.classList.remove(this.togglerActiveClass)
        );
        activeToggler.classList.add(this.togglerActiveClass);
    };

    @Watch("active")
    resetTogglers() {
        if (this.active === false) {
            const togglers = Array.from(
                this.togglerContainer.querySelectorAll("button")
            );
            togglers.forEach((toggler) => {
                toggler.classList.remove(this.togglerActiveClass);
                const descriptionText = toggler.querySelector("span");
                descriptionText.style.width = "0px";
            });
        }
    }

    render() {
        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--active`]: this.active,
                }}
            >
                <div class={`${this.baseClass}__content`}>
                    <div class={`${this.baseClass}__toggler-wrapper`}>
                        <div
                            class={{
                                [`${this.baseClass}__toggler-container`]: true,
                                [`${this.baseClass}__toggler-container--ready`]:
                                    this.togglerReady,
                                [`ux-u-shadow`]: true,
                            }}
                            ref={(el) => (this.togglerContainer = el)}
                        >
                            {this.togglerButtons.map(
                                (togglerButton: TogglerButton) => {
                                    return (
                                        <SidebarTogglerButton
                                            iconName={togglerButton.iconName}
                                            label={togglerButton.label}
                                            tabTarget={togglerButton.tabTarget}
                                            key={togglerButton.label}
                                            expanTogglerButton={
                                                this.expandTogglerButton
                                            }
                                            retractTogglerButton={
                                                this.retractTogglerButton
                                            }
                                            triggerActiveState={
                                                this.triggerActiveState
                                            }
                                        />
                                    );
                                }
                            )}
                        </div>
                    </div>
                    <div
                        class={`${this.baseClass}__sidebar-content ux-u-shadow`}
                    >
                        <div
                            class={`${this.baseClass}__content-shadow ${this.baseClass}__content-shadow--top`}
                        ></div>
                        <div class={`${this.baseClass}__tab-wrapper`}>
                            <slot />
                        </div>
                        <div
                            class={`${this.baseClass}__content-shadow ${this.baseClass}__content-shadow--bottom`}
                        ></div>
                    </div>
                </div>
            </Host>
        );
    }
}
